/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDuration, useDebounce, useQuery } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY } from '@/common/constants';
import { ValuesMap } from '@/common/types';
import { ReportTableResponse } from '@/modules/Reports/report.types';

import { useReportPreferencesHandler } from '../../hooks';
import { useReportTableBase, useReportTablePaginationBase } from '../hooks';
import { COLUMN_SIZE_MAP, DEFAULT_COLUMN_WIDTH } from '../hooks/useReportTableBase';

const PER_PAGE = 45;
interface TableParams {
  isPreviewMode?: boolean;
  disabled?: boolean;
  isMultipleEmployers?: boolean;
}
const useBaseReportTableQuery = (
  path: string,
  queryArray: any[],
  params: ValuesMap<any>,
  tableParams?: TableParams
) => {
  const { page, setPage, setRawHeaders, setRawContentList, rawHeaders, rawContentList } =
    useReportTablePaginationBase();

  const { data, isLoading, isSuccess, isFetching } = useQuery(
    [...queryArray, page],
    ({ signal }) =>
      api.get<ReportTableResponse>(path, {
        signal,
        params: _.pickBy({
          size: PER_PAGE,
          page,
          ...params,
        }),
      }),
    {
      onSuccess: (data) => {
        const content = data?.data?.content || [];
        const headers = data?.data?.headers || [];
        const pageNumber = data?.data?.page_number || 0;
        const headerDifference = _.differenceWith(headers, rawHeaders, _.isEqual);
        if (!rawHeaders.length) {
          setRawHeaders(headers);
        }
        if (headerDifference.length) {
          setRawHeaders([...rawHeaders, ...headerDifference]);
        }
        if (pageNumber === 0) {
          setRawContentList([...content]);
        } else {
          setRawContentList([...rawContentList, ...content]);
        }
      },
      cacheTime: 0,

      enabled: tableParams?.disabled ? false : !!params.employer_id || !!tableParams?.isMultipleEmployers,
    }
  );
  const total = useMemo(() => data?.data?.total_records || 0, [data?.data?.total_records]);

  const isHasNextPage = useMemo(() => data?.data?.has_next, [data?.data?.has_next]);

  const {
    data: preferencesData,
    handleSave: handleSavePreferences,
    isSuccess: isPreferencesSuccess,
  } = useReportPreferencesHandler();
  const [currentColumnsWidth, setCurrentColumnWidth] = useState(COLUMN_SIZE_MAP);

  useEffect(() => {
    if (preferencesData.columnWidth) {
      setCurrentColumnWidth(preferencesData.columnWidth);
    }
  }, [preferencesData.columnWidth]);

  const { headerKeys, columnWidths, lastTimeUpdateDate, headers, nodes, planOptions, onChangePlanOptions } =
    useReportTableBase({
      isLoading,
      isSSNShowed: params.isSSNShowed,
      contentList: rawContentList,
      rawHeaders: rawHeaders,
      columnsSize: currentColumnsWidth,
      rawPlans: data?.data?.plans || [],
      lastUpdatedAt: data?.data?.last_updated_at,
      isPreviewMode: tableParams?.isPreviewMode,
    });
  const handleLoadMore = useCallback(() => {
    if (PER_PAGE < total && !isLoading) {
      setPage(page + 1);
    }
  }, [isLoading, page, setPage, total]);

  const isLoadingApplied = useMemo(() => {
    return isLoading || (nodes.length === 0 && total > 0);
  }, [isLoading, nodes.length, total]);
  const isExportDisabled = useMemo(() => {
    if ((isSuccess && total === 0) || isLoading) {
      return true;
    }
    if (!tableParams?.isMultipleEmployers && !params.employer_id) {
      return true;
    }
    return false;
  }, [isLoading, isSuccess, params.employer_id, tableParams?.isMultipleEmployers, total]);

  const [tempColumnWidth, setTempColumnWidth] = useState(COLUMN_SIZE_MAP);
  const debouncedColumnWidth = useDebounce(tempColumnWidth, getDuration({ seconds: 1.5 }));
  useEffect(() => {
    if (isPreferencesSuccess && !isLoading) {
      handleSavePreferences({
        columnWidth: debouncedColumnWidth,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedColumnWidth, handleSavePreferences, isPreferencesSuccess]);
  const handleChangeHeaderSize = useCallback(
    (values: number[]) => {
      const columnMap = preferencesData.columnWidth || {};
      const currentValues: ValuesMap<number> = {
        ...COLUMN_SIZE_MAP,
        ...columnMap,
      };

      headerKeys.forEach((headerKey, index) => {
        currentValues[headerKey] =
          values[index] || columnMap[headerKey] || currentValues[headerKey] || DEFAULT_COLUMN_WIDTH;
      });
      if (isPreferencesSuccess && !isLoading) {
        setTempColumnWidth(currentValues);
      }
    },
    [headerKeys, isLoading, isPreferencesSuccess, preferencesData.columnWidth]
  );

  const isEmptyPlanOptions = useMemo(
    () => _.isEmpty(planOptions.filter((item) => item.key !== ALL_OPTION_KEY)),
    [planOptions]
  );

  return {
    setPage,
    setRawContentList,
    lastTimeUpdateDate,
    isEmptyPlanOptions,
    planOptions,
    columnWidths: columnWidths,
    headers,
    headerKeys,
    isHasNextPage,
    total,
    nodes,
    isSuccess,
    data,
    isLoading: isLoadingApplied,
    isFetching,
    handleLoadMore,
    isExportDisabled,
    setRawHeaders,
    onChangePlanOptions,
    onChangeHeaderSize: handleChangeHeaderSize,
  };
};

export default useBaseReportTableQuery;
